import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, RowsSkeletonLoader } from '@node-space/ui-components'
import { EarnDirectSummary } from './components/direct/EarnDirectSummary'
import { EarnAssetsList } from './components/EarnAssetsList'
import { EarnNoPortfolio } from './components/EarnNoPortfolio'
import { EarnNoProducts } from './components/EarnNoProducts'
import { EarnNoWallets } from './components/EarnNoWallets'
import { EarnPageHeader } from './components/EarnPageHeader'
import { EarnEmbeddedCustomerRewardsProgram } from './components/embedded/EarnEmbeddedCustomerRewardsProgram'
import { EarnEmbeddedDistributionDialog } from './components/embedded/EarnEmbeddedDistributionDialog'
import { EarnEmbeddedMyRewards } from './components/embedded/EarnEmbeddedMyRewards'
import { EarnSheetProvider, useEarnSheetContext } from './context/EarnSheetProvider'
import { useEarnDashboardData } from './hooks/useEarnDashboardData'
import { useEarnTracking } from './hooks/useEarnTracking'
import { EARN_DASHBOARD_STATE, EARN_MODEL, EARN_SHEET_ACTION_TYPE, EarnAsset } from './types'

export const Earn = () => (
  <EarnSheetProvider>
    <EarnContent />
  </EarnSheetProvider>
)

const EarnContent = () => {
  const { t } = useTranslation()
  const { trackPageView } = useEarnTracking()
  const { handleSheetOpen } = useEarnSheetContext()
  const [isDistributionDialogOpen, setIsDistributionDialogOpen] = useState(false)

  const handleDeposit = useCallback(
    (asset: EarnAsset) => {
      handleSheetOpen({
        type: EARN_SHEET_ACTION_TYPE.DEPOSIT,
        header: t('earn.depositSheet.header'),
        selectedCurrency: asset.asset,
        portfolioId: asset.portfolioId ?? undefined,
        productName: asset.productName,
      })
    },
    [handleSheetOpen, t]
  )

  const handleWithdraw = useCallback(
    (asset: EarnAsset) => {
      handleSheetOpen({
        type: EARN_SHEET_ACTION_TYPE.WITHDRAW,
        header: t('earn.withdrawSheet.header'),
        selectedCurrency: asset.asset,
        portfolioId: asset.portfolioId ?? undefined,
        productName: asset.productName,
      })
    },
    [handleSheetOpen, t]
  )

  const earnPageData = useEarnDashboardData({
    onDeposit: handleDeposit,
    onWithdraw: handleWithdraw,
  })
  const { isStatisticsLoading, earnModel, canManageDistribution } = earnPageData
  const isDirectEarning = earnModel === EARN_MODEL.DIRECT

  useEffect(() => {
    trackPageView('home')
  }, [trackPageView])

  if (earnPageData.dashboardState === EARN_DASHBOARD_STATE.LOADING) {
    return <RowsSkeletonLoader rows={10} />
  }

  if (earnPageData.dashboardState === EARN_DASHBOARD_STATE.NO_PRODUCTS) {
    return <EarnNoProducts />
  }

  if (earnPageData.dashboardState === EARN_DASHBOARD_STATE.NO_WALLETS) {
    return <EarnNoWallets featuredProduct={earnPageData.featuredProduct} />
  }

  if (earnPageData.dashboardState === EARN_DASHBOARD_STATE.NO_PORTFOLIOS) {
    return <EarnNoPortfolio assets={earnPageData.assets} showCustomerEarnRate={!isDirectEarning} />
  }

  const {
    assets,
    epcTakesEarn,
    distributionProductId,
    summary,
    totalEarnings,
    customerRewardsProgram,
  } = earnPageData

  if (isDirectEarning) {
    return (
      <>
        <EarnPageHeader />
        <Box flexCol gap={24}>
          <EarnDirectSummary
            allocatedFunds={summary.allocatedFunds}
            availableFunds={summary.availableFunds}
            totalAllTimeEarnings={summary.totalAllTimeEarnings}
            isStatisticsLoading={isStatisticsLoading}
          />
          <EarnAssetsList assets={assets} showCustomerEarnRate={false} />
        </Box>
      </>
    )
  }

  return (
    <>
      <EarnPageHeader />
      <Box flexCol gap={24}>
        <Box flexCol gap={24} className="items-stretch lg:flex-row">
          <Box className="flex-1 min-w-0">
            <EarnEmbeddedMyRewards
              allocatedFunds={summary.allocatedFunds}
              availableFunds={summary.availableFunds}
              isStatisticsLoading={isStatisticsLoading}
              totalEarnings={totalEarnings.totalEarnings}
              allocationEarnings={totalEarnings.allocationEarnings}
              customerEarnings={totalEarnings.customerEarnings}
            />
          </Box>
          <Box className="flex-1 min-w-0">
            <EarnEmbeddedCustomerRewardsProgram
              onDistributeEarningsClick={() => setIsDistributionDialogOpen(true)}
              epcTakesEarn={epcTakesEarn}
              canManageDistribution={canManageDistribution}
              isStatisticsLoading={isStatisticsLoading}
              {...customerRewardsProgram}
            />
          </Box>
        </Box>
        <EarnAssetsList assets={assets} showCustomerEarnRate={!isDirectEarning} />
        {distributionProductId && (
          <EarnEmbeddedDistributionDialog
            open={isDistributionDialogOpen}
            onClose={() => setIsDistributionDialogOpen(false)}
            productId={distributionProductId}
          />
        )}
      </Box>
    </>
  )
}
